import { useState, useRef } from 'react';
import axios from 'axios';
import { UploadSimple, X, PencilSimple } from '@phosphor-icons/react';
import BannerCropAdjuster from './BannerCropAdjuster';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Poster/banner upload with post-upload focal-point picker.
 *   value       = current banner URL (string)
 *   focusX/Y    = current focus percentages (numbers, default 50)
 *   onChange    = (url) => void  — url string on upload/clear
 *   onFocusChange = (fx, fy) => void — focus % on every crop adjust
 * Any aspect ratio is accepted. The card thumbnail uses the focus values; the
 * details page shows the full untouched image.
 */
const BannerUpload = ({ value, focusX = 50, focusY = 50, onChange, onFocusChange }) => {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [showAdjuster, setShowAdjuster] = useState(false);
  const fileRef = useRef(null);

  const onFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setError('');
    if (!['image/jpeg', 'image/png', 'image/webp', 'image/gif'].includes(file.type)) { setError('Use JPG, PNG, WebP, or GIF'); return; }
    if (file.size > 5 * 1024 * 1024) { setError('Image must be under 5MB'); return; }

    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const { data } = await axios.post(`${API}/upload/banner`, fd, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
      onChange(data.url);
      // Fresh upload → reset focus to center and immediately open the adjuster.
      onFocusChange && onFocusChange(50, 50);
      setShowAdjuster(true);
    } catch (err) { setError(err.response?.data?.detail || 'Upload failed'); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ''; }
  };

  const handleClear = () => {
    onChange('');
    onFocusChange && onFocusChange(50, 50);
    setShowAdjuster(false);
  };

  return (
    <div>
      <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" onChange={onFile} className="hidden" data-testid="banner-file-input" />

      {value ? (
        <div className="space-y-3">
          {/* Live tournament-card style thumbnail (uses focus). */}
          <div className="relative rounded-xl overflow-hidden border border-gray-200 bg-gray-900" style={{ aspectRatio: '16 / 9' }}>
            <img
              src={value}
              alt="Banner"
              className="absolute inset-0 w-full h-full"
              style={{ objectFit: 'cover', objectPosition: `${focusX}% ${focusY}%` }}
              data-testid="banner-preview"
            />
            <button
              type="button"
              onClick={handleClear}
              className="absolute top-2 right-2 bg-white/95 hover:bg-red-500 hover:text-white text-gray-700 p-2 rounded-lg transition-colors"
              data-testid="banner-clear-btn"
              aria-label="Remove banner"
            >
              <X size={14} weight="bold" />
            </button>
            <button
              type="button"
              onClick={() => setShowAdjuster((v) => !v)}
              className="absolute bottom-2 right-2 inline-flex items-center gap-1.5 bg-white/95 hover:bg-[#F97316] hover:text-white text-gray-800 text-[11px] font-semibold px-2.5 py-1.5 rounded-lg transition-colors"
              data-testid="banner-adjust-btn"
            >
              <PencilSimple size={12} weight="bold" />
              {showAdjuster ? 'Done' : 'Adjust Thumbnail'}
            </button>
          </div>

          {showAdjuster && (
            <BannerCropAdjuster
              src={value}
              focusX={focusX}
              focusY={focusY}
              onChange={(fx, fy) => onFocusChange && onFocusChange(fx, fy)}
            />
          )}
        </div>
      ) : (
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          className="w-full bg-gray-50 hover:bg-orange-50 border-2 border-dashed border-gray-200 hover:border-[#F97316] text-gray-500 hover:text-[#F97316] rounded-xl p-8 transition-colors flex flex-col items-center justify-center gap-2"
          data-testid="banner-upload-btn"
        >
          <UploadSimple size={24} weight="bold" />
          <span className="text-sm font-semibold">{uploading ? 'Uploading...' : 'Upload Poster or Banner'}</span>
          <span className="text-xs text-gray-400">Any image size — JPG, PNG, WebP, GIF · max 5MB</span>
        </button>
      )}

      {/* Recommended-size hint. Non-blocking; upload accepts any dimensions. */}
      <p className="text-[11px] text-gray-500 mt-2" data-testid="banner-size-hint">
        <span className="font-semibold text-gray-600">Recommended:</span> 1200 × 675 px (16:9, landscape) for the sharpest card thumbnail — but portrait, square, and Instagram-post sizes work too. You&apos;ll set the visible crop after upload.
      </p>

      {error && <p className="text-red-600 text-xs mt-2" data-testid="banner-error">{error}</p>}
    </div>
  );
};

export default BannerUpload;
